/** First sign-in: pick a template (4-list method / Simple / Import from ClickUp) and "My day starts at". */
import { useState } from 'react';
import { Check, Download, LayoutList, ListChecks, X } from 'lucide-react';
import { METHOD_TEMPLATE, SIMPLE_TEMPLATE, isValidTimeZone } from '@karya/core';
import { cn } from '@/lib/cn';
import { Button } from '@/components/ui/button';
import * as m from '@/data/mutations';

type Choice = 'method' | 'simple' | 'import';

const ZONES = [
  'Asia/Kolkata',
  'Asia/Dubai',
  'Asia/Singapore',
  'Europe/London',
  'Europe/Berlin',
  'America/New_York',
  'America/Los_Angeles',
  'Australia/Sydney',
  'UTC',
];

export function Onboarding({ onDone }: { onDone: (goImport: boolean) => void }) {
  const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  const [choice, setChoice] = useState<Choice>('method');
  const [names, setNames] = useState<string[]>(['Personal']);
  const [draft, setDraft] = useState('');
  const [dayStart, setDayStart] = useState('09:00');
  const [tz, setTz] = useState(browserTz);
  const [busy, setBusy] = useState(false);

  const addName = () => {
    const v = draft.trim();
    if (v && !names.includes(v)) setNames([...names, v]);
    setDraft('');
  };
  const finish = async () => {
    setBusy(true);
    try {
      const finalNames = draft.trim() ? [...names, draft.trim()] : names;
      if (choice === 'import') {
        await m.updateProfile({
          day_start_time: dayStart,
          timezone: tz,
          onboarded_at: new Date().toISOString(),
        });
      } else {
        await m.applyTemplate(choice === 'method' ? METHOD_TEMPLATE : SIMPLE_TEMPLATE, {
          spaceNames: choice === 'method' ? finalNames : undefined,
          dayStart,
          timezone: tz,
        });
      }
      onDone(choice === 'import');
    } finally {
      setBusy(false);
    }
  };

  const card = (key: Choice, Icon: typeof ListChecks, title: string, body: string) => (
    <button
      onClick={() => setChoice(key)}
      aria-pressed={choice === key}
      className={cn(
        'flex w-full items-start gap-3 rounded-md border p-4 text-left transition-colors',
        choice === key ? 'border-primary bg-primary-soft' : 'border-line hover:bg-surface-2',
      )}
    >
      <Icon
        className={cn('mt-0.5 size-5 shrink-0', choice === key ? 'text-primary' : 'text-ink-2')}
      />
      <span className="flex-1">
        <span className="block font-semibold">{title}</span>
        <span className="block text-sm text-ink-2">{body}</span>
      </span>
      {choice === key && <Check className="size-5 text-primary" />}
    </button>
  );

  return (
    <main className="flex min-h-full justify-center overflow-y-auto bg-surface-1 p-4">
      <div className="my-auto flex w-full max-w-lg flex-col gap-5 rounded-lg border border-line bg-raised p-6 shadow-card sm:p-8">
        <div className="flex items-center gap-3">
          <img src="/icons/icon.svg" alt="" width={40} height={40} />
          <div>
            <h1 className="text-xl font-semibold">Welcome to Karya</h1>
            <p className="text-sm text-ink-2">
              Set up your workspace. You can change everything later.
            </p>
          </div>
        </div>
        <div className="flex flex-col gap-2">
          {card(
            'method',
            LayoutList,
            '4-list method',
            'Name your Spaces. Each gets 🎯 Priority Tasks · ⚡ Quick Wins · 🔁 Follow-ups · 📦 Backlog, with TO DO / IN PROGRESS / WAITING / COMPLETE and matching tags.',
          )}
          {card('simple', ListChecks, 'Simple', 'One Space with one list. Add more any time.')}
          {card(
            'import',
            Download,
            'Import from ClickUp',
            'Bring your spaces, lists and tasks over from ClickUp (API token or CSV export).',
          )}
        </div>
        {choice === 'method' && (
          <div className="flex flex-col gap-2">
            <label htmlFor="space-name" className="text-sm font-semibold">
              Your Spaces
            </label>
            <div className="flex flex-wrap gap-1.5">
              {names.map((n) => (
                <span
                  key={n}
                  className="inline-flex h-7 items-center gap-1 rounded-sm bg-surface-2 px-2 text-sm"
                >
                  {n}
                  <button
                    aria-label={`Remove ${n}`}
                    onClick={() => setNames(names.filter((x) => x !== n))}
                    className="text-ink-3 hover:text-ink"
                  >
                    <X className="size-3.5" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                id="space-name"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addName())}
                placeholder="e.g. Work, Home, Side project"
                className="h-9 flex-1 rounded-sm border border-line bg-bg px-2 outline-none focus:border-primary"
              />
              <Button onClick={addName} disabled={!draft.trim()}>
                Add
              </Button>
            </div>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3 max-sm:grid-cols-1">
          <label className="flex flex-col gap-1 text-sm font-semibold">
            My day starts at
            <input
              type="time"
              value={dayStart}
              onChange={(e) => setDayStart(e.target.value)}
              className="h-9 rounded-sm border border-line bg-bg px-2 font-normal outline-none focus:border-primary"
            />
            <span className="text-cap font-normal text-ink-3">
              Reminders for tasks without a time, the daily summary and “tomorrow morning” snoozes
              use this.
            </span>
          </label>
          <label className="flex flex-col gap-1 text-sm font-semibold">
            Time zone
            <select
              value={tz}
              onChange={(e) => setTz(e.target.value)}
              className="h-9 rounded-sm border border-line bg-bg px-2 font-normal outline-none focus:border-primary"
            >
              {[...new Set([browserTz, ...ZONES])].filter(isValidTimeZone).map((z) => (
                <option key={z} value={z}>
                  {z}
                </option>
              ))}
            </select>
          </label>
        </div>
        <Button
          variant="primary"
          size="lg"
          disabled={busy || (choice === 'method' && names.length === 0 && !draft.trim())}
          onClick={() => void finish()}
        >
          {choice === 'import' ? 'Continue to import' : 'Create my workspace'}
        </Button>
      </div>
    </main>
  );
}
